import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCloudDownload, cilX } from "@coreui/icons";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import { triggerDownloadFromUrl } from "views/applications/_utils/applicationPdfExport";

const PROGRAMAS = [
  { value: "inovacao", label: "SICE - Inovação Produtiva" },
  { value: "internacionalizacao", label: "SICE - Internacionalização" },
];

const RESPOSTAS_LABELS = {
  designacao: "Designação do projeto / Nome da Operação",
  data_kickoff: "Data do kick-off",
  data_decisao_final: "Data de emissão da decisão final",
  email_gestao: "E-mail do consultor de gestão",
  email_elaboracao: "E-mail do consultor de elaboração",
};

const EMAIL_DOMAIN = "@streamconsulting.pt";

// decisão do utilizador: o download deixa de estar disponível 20h depois
// da geração (mesma regra aplicada no backend, ver KickoffDeckJobDetailView
// — `download_expirado` na resposta é a fonte de verdade; isto aqui é só
// para esconder o botão sem esperar por um novo pedido à API).
const DOWNLOAD_DISPONIVEL_HORAS = 20;

const isDownloadExpirado = (job, now) => {
  if (job.download_expirado !== undefined) return job.download_expirado;
  if (!job.created_at) return false;
  const criado = new Date(job.created_at).getTime();
  return now - criado > DOWNLOAD_DISPONIVEL_HORAS * 60 * 60 * 1000;
};

// <input type="date"> devolve sempre "AAAA-MM-DD" — o template espera
// "DD/MM/AAAA" (mesmo formato usado em todo o resto do kick-off).
const isoToPtDate = (iso) => {
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
};

const STATUS_BADGE = {
  PENDING: { color: "secondary", label: "Em fila" },
  IN_PROGRESS: { color: "info", label: "A gerar" },
  COMPLETED: { color: "success", label: "Concluído" },
  FAILED: { color: "danger", label: "Falhou" },
};

const emptyForm = {
  programa: "inovacao",
  designacao: "",
  data_kickoff: "",
  data_decisao_final: "",
  email_gestao: "",
  email_elaboracao: "",
};

const KickoffDeckList = () => {
  const [toast, setToast] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewJob, setPreviewJob] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const fileInputRef = useRef(null);

  // reavalia a expiração do download (20h) mesmo sem nenhum job pendente a
  // fazer polling — sem isto o botão só desaparecia num próximo reload.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchJobs = useCallback(async ({ silent = false } = {}) => {
    try {
      const response = await apiClient.get("/kickoff-deck/");
      setJobs(response.data || []);
      return response.data || [];
    } catch (error) {
      if (!silent) {
        setToast({
          title: "Kickoff",
          message: "Erro a carregar o histórico de apresentações geradas",
          type: "error",
        });
      }
      return [];
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchJobs().finally(() => setIsLoading(false));
  }, [fetchJobs]);

  // Faz polling enquanto houver algum job em PENDING/IN_PROGRESS — o pptx é
  // gerado de forma assíncrona (fila Celery dedicada "kickoff") para não
  // bloquear o pedido nem outros processos. Um único GET à lista (em vez de
  // um GET por job pendente) — o histórico já devolve o estado de todos.
  useEffect(() => {
    const temPendentes = jobs.some((j) =>
      ["PENDING", "IN_PROGRESS"].includes(j.status),
    );
    if (!temPendentes) return undefined;

    const timer = setInterval(() => {
      fetchJobs({ silent: true });
    }, 3000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  const handleFieldChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      setToast({
        title: "Kickoff",
        message: "Escolhe o PDF de Análise/Parecer.",
        type: "warning",
      });
      return;
    }

    const formData = new FormData();
    formData.append("programa", form.programa);
    formData.append("source_pdf", pdfFile);
    if (form.designacao) formData.append("designacao", form.designacao);
    if (form.data_kickoff) {
      formData.append("data_kickoff", isoToPtDate(form.data_kickoff));
    }
    if (form.data_decisao_final) {
      formData.append(
        "data_decisao_final",
        isoToPtDate(form.data_decisao_final),
      );
    }
    if (form.email_gestao) {
      formData.append("email_gestao", `${form.email_gestao}${EMAIL_DOMAIN}`);
    }
    if (form.email_elaboracao) {
      formData.append(
        "email_elaboracao",
        `${form.email_elaboracao}${EMAIL_DOMAIN}`,
      );
    }

    setSubmitting(true);
    try {
      const response = await apiClient.post("/kickoff-deck/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobs((prev) => [response.data, ...prev]);
      setForm(emptyForm);
      setPdfFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setToast({
        title: "Kickoff",
        message:
          "Geração da apresentação iniciada — acompanha o estado na tabela.",
        type: "success",
      });
    } catch (error) {
      setToast({
        title: "Kickoff",
        message:
          error?.response?.data?.detail ||
          "Erro ao iniciar a geração do kick-off.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (job) => {
    try {
      const response = await apiClient.get(`/kickoff-deck/${job.id}/`);
      if (response.data.download_url) {
        await triggerDownloadFromUrl(
          response.data.download_url,
          response.data.output_filename || job.output_filename,
        );
      }
    } catch (error) {
      setToast({
        title: "Kickoff",
        message: "Erro a obter o link de download.",
        type: "error",
      });
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("pt-PT");
  };

  const programaLabel = (value) =>
    PROGRAMAS.find((p) => p.value === value)?.label || value;

  const columns = [
    {
      name: "Data",
      selector: (row) => formatDateTime(row.created_at),
      sortable: true,
    },
    {
      name: "Programa",
      selector: (row) => programaLabel(row.programa),
      sortable: true,
    },
    {
      name: "Ficheiro",
      selector: (row) => row.output_filename || row.source_filename || "-",
      sortable: true,
      grow: 2,
    },
    {
      name: "Estado",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const badge = STATUS_BADGE[row.status] || {
          color: "secondary",
          label: row.status,
        };
        return (
          <CBadge color={badge.color}>
            {["PENDING", "IN_PROGRESS"].includes(row.status) && (
              <CSpinner size="sm" className="me-1" />
            )}
            {badge.label}
          </CBadge>
        );
      },
    },
    {
      name: "Criado por",
      selector: (row) => row.created_by_name || "-",
      sortable: true,
    },
    {
      name: "",
      button: true,
      cell: (row) => {
        if (row.status !== "COMPLETED" || isDownloadExpirado(row, now)) {
          return null;
        }
        return (
          <CButton
            color="primary"
            size="sm"
            className="text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(row);
            }}
          >
            <CIcon icon={cilCloudDownload} className="me-1" />
            Descarregar
          </CButton>
        );
      },
    },
  ];

  const camposOmissos = previewJob
    ? Object.keys(RESPOSTAS_LABELS).filter(
        (field) => !(previewJob.respostas || {})[field],
      )
    : [];

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard className="mb-4">
        <CCardHeader>
          <h3 className="mb-0">Kickoff</h3>
          <div className="text-medium-emphasis small">
            Gera a apresentação de kick-off (.pptx) a partir do PDF de
            Análise/Parecer da candidatura.
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <CRow className="g-3">
              <CCol md={4}>
                <CFormLabel>Tipo de projeto</CFormLabel>
                <CFormSelect
                  value={form.programa}
                  onChange={handleFieldChange("programa")}
                >
                  {PROGRAMAS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>
              <CCol md={8}>
                <CFormLabel>PDF de Análise/Parecer</CFormLabel>
                <CFormInput
                  type="file"
                  accept="application/pdf"
                  ref={fileInputRef}
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  required
                />
              </CCol>

              <CCol xs={12}>
                <hr className="my-2" />
                <div className="text-medium-emphasis small mb-2">
                  Campos sem fonte no PDF — o que deixares em branco fica
                  marcado no ficheiro gerado com "❓ PREENCHER" para reveres
                  depois.
                </div>
              </CCol>

              <CCol md={form.programa === "inovacao" ? 4 : 6}>
                <CFormLabel>Designação do projeto (opcional)</CFormLabel>
                <CFormInput
                  value={form.designacao}
                  onChange={handleFieldChange("designacao")}
                />
              </CCol>
              <CCol md={form.programa === "inovacao" ? 4 : 6}>
                <CFormLabel>Data do kick-off (opcional)</CFormLabel>
                <CFormInput
                  type="date"
                  value={form.data_kickoff}
                  onChange={handleFieldChange("data_kickoff")}
                />
              </CCol>
              {form.programa === "inovacao" && (
                <CCol md={4}>
                  <CFormLabel>
                    Data de emissão da decisão final (opcional)
                  </CFormLabel>
                  <CFormInput
                    type="date"
                    value={form.data_decisao_final}
                    onChange={handleFieldChange("data_decisao_final")}
                  />
                </CCol>
              )}
              <CCol md={6}>
                <CFormLabel>
                  E-mail do consultor de gestão (opcional)
                </CFormLabel>
                <CInputGroup>
                  <CFormInput
                    value={form.email_gestao}
                    onChange={handleFieldChange("email_gestao")}
                    placeholder="nome.apelido"
                  />
                  <CInputGroupText>{EMAIL_DOMAIN}</CInputGroupText>
                </CInputGroup>
              </CCol>
              <CCol md={6}>
                <CFormLabel>
                  E-mail do consultor de elaboração (opcional)
                </CFormLabel>
                <CInputGroup>
                  <CFormInput
                    value={form.email_elaboracao}
                    onChange={handleFieldChange("email_elaboracao")}
                    placeholder="nome.apelido"
                  />
                  <CInputGroupText>{EMAIL_DOMAIN}</CInputGroupText>
                </CInputGroup>
              </CCol>

              <CCol xs={12}>
                <CButton
                  color="info"
                  className="text-white"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting && <CSpinner size="sm" className="me-2" />}
                  Gerar apresentação
                </CButton>
                <div className="text-medium-emphasis small mb-2">
                  Este documento é gerado por um script de automatização, pode
                  conter erros, confirme o documento.
                </div>
              </CCol>
            </CRow>
          </CForm>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <h5 className="mb-0">Histórico</h5>
        </CCardHeader>
        <CCardBody>
          {isLoading ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataTableComponent
              columns={columns}
              data={jobs}
              onRowClicked={(row) => setPreviewJob(row)}
            />
          )}
        </CCardBody>
      </CCard>

      <COffcanvas
        placement="end"
        visible={Boolean(previewJob)}
        onHide={() => setPreviewJob(null)}
        style={{ width: "480px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle>Kick-off #{previewJob?.id}</COffcanvasTitle>
          <CButton
            color="link"
            className="text-body p-0"
            onClick={() => setPreviewJob(null)}
          >
            <CIcon icon={cilX} size="lg" />
          </CButton>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewJob && (
            <div className="d-flex flex-column gap-3">
              {previewJob.status === "FAILED" && previewJob.error && (
                <div className="border rounded-3 p-3 bg-danger-subtle">
                  <div className="text-uppercase text-medium-emphasis small">
                    Erro
                  </div>
                  <div className="fw-semibold">{previewJob.error}</div>
                </div>
              )}

              {camposOmissos.length > 0 && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Campos que ficaram em branco
                  </div>
                  <ul className="mb-0 mt-2">
                    {camposOmissos.map((field) => (
                      <li key={field}>{RESPOSTAS_LABELS[field]}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(previewJob.avisos || []).length > 0 && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Pontos a rever
                  </div>
                  <ul className="mb-0 mt-2">
                    {previewJob.avisos.map((aviso, idx) => (
                      <li key={idx}>{aviso}</li>
                    ))}
                  </ul>
                </div>
              )}

              {previewJob.sempre_manuais && (
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Sempre manuais, por desenho
                  </div>
                  <div className="mt-2 small">{previewJob.sempre_manuais}</div>
                </div>
              )}

              {previewJob.status === "COMPLETED" &&
                !isDownloadExpirado(previewJob, now) && (
                  <CButton
                    color="primary"
                    className="text-white"
                    onClick={() => handleDownload(previewJob)}
                  >
                    <CIcon icon={cilCloudDownload} className="me-1" />
                    Descarregar
                  </CButton>
                )}
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default KickoffDeckList;
